import React, { useCallback, useEffect, useRef } from "react";
import embed from "vega-embed";

import ButtonSimple from "@/components/ButtonSimple";
import TooltipCustom from "@/components/TooltipCustom";
import { IconAiOutlineDownload } from "@/utils/icons";
import { lineBreakerForChartTitle } from "@/utils";

const ChartBarHorizontal = (props: any) => {

  const { item } = props;
  const chartRef: React.RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null);
  const chartData = item?.data;
  const legendTitle = item?.legend_title || '';
  const resizeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const viewRef = useRef<any>(null);

  const handleExport = useCallback(() => {
    if (viewRef.current) {
      viewRef.current.toImageURL('png', 2).then((url: string) => {
        const link = document.createElement('a');
        link.download = `${item?.title || 'chart'}.png`;
        link.href = url;
        link.click();
      });
    }
  }, [item?.title]);

  const renderChart = useCallback(() => {

    if (!chartRef.current || !chartData) {
      return;
    }

    const chartWidth = chartRef.current.offsetWidth > 350 ? chartRef.current.offsetWidth : 350;
    const title = item?.title || "";
    const formattedTitle = lineBreakerForChartTitle(title, chartWidth);

    const spec: any = {
      "$schema": "https://vega.github.io/schema/vega/v5.json",
      "description": item?.description || "",
      "width": chartWidth,
      "height": 500,
      "padding": { "top": 0, "bottom": 0, "left": 0, "right": 10 },
      "autosize": { "type": "fit", "contains": "padding" },
      "config": {
        "title": { "font": "Arial", "fontSize": 12 },
        "text": { "font": "Arial", "fontSize": 10 },
        "axis": { "labelFont": "Arial", "labelFontSize": "10", "labelColor": "gray" }
      },
      "title": {
        "text": formattedTitle,
        "fontSize": 12,
        "fontFamily": "Arial",
        "fontWeight": "bold",
        "color": "black",
        "anchor": "middle",
        "lineHeight": 14
      },
      "data": [
        {
          "name": "table",
          "values": chartData
        }
      ],

      "signals": [
        {
          "name": "tooltip",
          "value": {},
          "on": [
            { "events": "rect:pointerover", "update": "datum" },
            { "events": "rect:pointerout", "update": "{}" }
          ]
        }
      ],

      "scales": [
        {
          "name": "yscale",
          "type": "band",
          "domain": { "data": "table", "field": "category" },
          "range": "height",
          "padding": 0.3,
          "round": true
        },
        {
          "name": "xscale",
          "domain": { "data": "table", "field": "data_val" },
          "nice": true,
          "range": "width"
        },
        {
          "name": "color",
          "type": "ordinal",
          "domain": { "data": "table", "field": "category" },
          "range": { "data": "table", "field": "colorParam" }
        }
      ],

      "axes": [
        { "orient": "bottom", "scale": "xscale" },
        { "orient": "left", "scale": "yscale" }
      ],

      "marks": [
        {
          "type": "rect",
          "from": { "data": "table" },
          "encode": {
            "enter": {
              "y": { "scale": "yscale", "field": "category" },
              "height": { "scale": "yscale", "band": 1 },
              "x": { "scale": "xscale", "value": 0 },
              "x2": { "scale": "xscale", "field": "data_val" }
            },
            "update": {
              "fill": { "scale": "color", "field": "category" }
            },
          }
        },
        {
          "type": "text",
          "from": { "data": "table" },
          "encode": {
            "update": {
              "y": { "scale": "yscale", "field": "category", "band": 0.5 },
              "x": { "scale": "xscale", "field": "data_val", "offset": 8 },
              "align": { "value": "left" },
              "baseline": { "value": "middle" },
              "fill": { "value": "#333" },
              "text": { "field": "data_val" }
            }
          }
        }
      ]
    }

    if (legendTitle) {
      Object.assign(spec, {
        "legends": [
          {
            "fill": "color",
            "title": legendTitle,
            "orient": "right",
            "titleFont": "Arial",
            "padding": 0,
            "titleFontSize": 12,
            "labelFont": "Arial",
            "labelFontSize": 12
          }
        ],
      })
    }

    embed(chartRef.current as unknown as HTMLElement, spec, {
      actions: {
        export: true, // enable export menu
        source: false, // hide "View Source"
        compiled: false, // hide "View Compiled"
        editor: false // hide "Open in Vega Editor"
      }
    }).then((horizontalView: any) => {
      viewRef.current = horizontalView.view;
      const menu = chartRef.current?.querySelector(".vega-actions");
      if (menu) {
        const svgButton = menu.querySelector('a[title="Save as SVG"]');
        if (svgButton) {
          svgButton.remove();
        }
      }
    });

  }, [item]);

  // Debounced resize handler
  const handleResize = useCallback(() => {
    if (resizeTimeoutRef.current) {
      clearTimeout(resizeTimeoutRef.current);
    }

    resizeTimeoutRef.current = setTimeout(() => {
      renderChart();
    }, 300);
  }, [renderChart]);


  useEffect(() => {

    renderChart();

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
    };
  }, [renderChart, handleResize]);


  return (
    <div>
      <TooltipCustom title="Export as PNG">
        <ButtonSimple
          onClick={handleExport}
          className='flex justify-self-end mb-1'
        >
          <IconAiOutlineDownload />
        </ButtonSimple>
      </TooltipCustom>
      <div id="chart-bar-horizontal" className="!p-0 !pr-0 !pe-0 " ref={chartRef}></div>
    </div>
  );
}


export default ChartBarHorizontal;